import Typography from '@/common/components/base/typography/Typography';
import UploadDocumentCameraPage from '@/common/components/camera/pages/UploadDocumentCameraPage';
import EditImage from '@/common/components/imageGallery/components/editImage/EditImage';
import { ImageType } from '@/common/components/imageGallery/components/Extensions/moreItemsExtension/moreItemsExtension.type';
import ImageCard from '@/common/components/imageGallery/components/ImageCard';
import UploadDocumentImageConfirmation from '@/common/components/uploadImage/UploadDocumentImageConfirmation';
import UploadImageCard from '@/common/components/uploadImage/UploadImageCard';
import useModal from '@/common/hooks/useModal';
import {
  ApiTabTypeProps,
  FileRoleTypeProps,
} from '@/common/types/ApiResponseTypes.type';
import { ImagesListName } from '@/common/utils/ImagesListName';
import { deleteDamagedAdjusterAttachedImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/deleteDamagedAdjusterAttachedImages/deleteDamagedAdjusterAttachedImagesQueryOptions';
import { getDamagedAdjusterAttachedImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDamagedAdjusterAttachedImages/getDamagedAdjusterAttachedImagesQueryOptions';
import { patchDamagedAdjusterAttachedImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchDamagedAdjusterAttachedImages/patchDamagedAdjusterAttachedImagesQueryOptions';
import { postAdjusterAttachedImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/postDamagedAdjusterAttachedImages/postAdjusterAttachedImagesQueryOptions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';

const DamagedAttachedImagesTab = ({
  userType,
  TabType,
  title,
}: FileRoleTypeProps & ApiTabTypeProps & { title: string }) => {
  const { id, damagedId } = useParams();
  const queryClient = useQueryClient();
  const [retakePhotoId, setRetakePhotoId] = useState<string | undefined>(
    undefined,
  );
  const [uploadImageData, setUploadImageData] = useState<ImageType | undefined>(
    undefined,
  );
  const {
    closeModal: previewBeforeUploadClose,
    open: previewBeforeUploadOpen,
    openModal: previewBeforeUploadOpenHandler,
  } = useModal();
  const {
    closeModal: editUploadedImageClose,
    open: editUploadedImageOpen,
    openModal: editUploadedImageOpenHandler,
  } = useModal();
  const {
    closeModal: retakeUploadedImageClose,
    open: retakeUploadedImageOpen,
    openModal: retakeUploadedImageOpenHandler,
  } = useModal();
  const {
    closeModal: retakeNewImageClose,
    open: retakeNewImageOpen,
    openModal: retakeNewImageOpenHandler,
  } = useModal();

  const { mutateAsync: uploadImage, isPending: uploadLoading } = useMutation(
    postAdjusterAttachedImagesQueryOptions({
      type: TabType,
      fileType: userType,
    }),
  );
  const { mutateAsync: deleteImage, isPending: deleteImageLoading } =
    useMutation(
      deleteDamagedAdjusterAttachedImagesQueryOptions({
        type: TabType,
        fileType: userType,
      }),
    );

  const { mutateAsync: patchImage } = useMutation(
    patchDamagedAdjusterAttachedImagesQueryOptions({
      type: TabType,
      fileType: userType,
    }),
  );
  const { data: imagesData, isFetching } = useQuery(
    getDamagedAdjusterAttachedImagesQueryOptions({
      id: id!,
      damaged_id: damagedId,
      type: TabType,
      fileType: userType,
    }),
  );
  const invalidateGetList = () => {
    queryClient.invalidateQueries({
      queryKey: getDamagedAdjusterAttachedImagesQueryOptions({
        id: id!,
        damaged_id: damagedId,
        type: TabType,
        fileType: userType,
      }).queryKey,
    });
  };
  const uploadImageHandler = ({
    imageData,
    closeModal,
  }: {
    imageData: ImageType;
    closeModal?: () => void;
  }) => {
    uploadImage({
      id: Number(id),
      damagedId: Number(damagedId),
      data: { image: imageData.blob },
    }).then(() => {
      invalidateGetList();
      closeModal?.();
    });
  };

  const editAndUploadImage = ({
    imageData,
    closeModal,
    imageId,
  }: {
    imageData: ImageType;
    imageId: string;
    closeModal?: () => void;
  }) => {
    patchImage({
      id: Number(id),
      damagedId: Number(damagedId),
      data: { image: imageData.blob, image_id: imageId },
    }).then(() => {
      invalidateGetList();
      closeModal?.();
    });
  };

  return (
    <>
      <Typography textStyle="b1" color="purple-1" my="24px">
        {title}
      </Typography>
      <Flex
        gap="1rem"
        maxWidth="maxContainer"
        margin="auto"
        mt="1.5rem"
        className={grid({ columns: 2 })}
      >
        <UploadImageCard
          selectFromGallery
          title="افزودن تصویر"
          onUploadImage={({ imageData, closeModal }) => {
            previewBeforeUploadOpenHandler();
            closeModal();
            setUploadImageData(imageData);
          }}
          uploadImageLoading={uploadLoading || isFetching}
        />
        {imagesData?.vehicle?.map((vehicleImages, index) => {
          return (
            <ImageCard
              key={vehicleImages.id}
              imageData={{
                name: ImagesListName({
                  titleType: TabType === 'document' ? 'document' : 'attachment',
                  index,
                }),
                src: vehicleImages.image,
              }}
              extension={{
                type: 'moreItems',
                items: [
                  'deletePhoto',
                  'downloadPhoto',
                  'editPhoto',
                  'retakePhoto',
                ],
                handleClick: {
                  onEditSuccess: ({ data, closeModal }) => {
                    editAndUploadImage({
                      imageData: data,
                      closeModal,
                      imageId: vehicleImages.id.toString(),
                    });
                  },
                  retakePhotoOnClick: () => {
                    retakeUploadedImageOpenHandler();
                    setRetakePhotoId(vehicleImages.id.toString());
                  },
                  deletePhotoOnClick: ({ closeModal: closeDeleteModal }) => {
                    deleteImage({
                      id: Number(id),
                      damagedId: Number(damagedId),
                      data: { image_id: vehicleImages.id?.toString()! },
                    }).then(() => {
                      closeDeleteModal();
                      invalidateGetList();
                    });
                  },
                },
                deleteImageLoading: deleteImageLoading,
              }}
            />
          );
        })}
      </Flex>
      {imagesData?.damaged_parts?.length ? (
        <>
          <Typography textStyle="b1" color="purple-1" my="24px">
            تصاویر نقاط آسیب‌دیده
          </Typography>
          <Flex
            gap="1rem"
            maxWidth="maxContainer"
            margin="auto"
            mt="1.5rem"
            className={grid({ columns: 2 })}
          >
            {imagesData?.damaged_parts?.map((vehicleImages) => {
              return (
                <ImageCard
                  key={vehicleImages.id}
                  imageData={{
                    name: vehicleImages.title?.fa_const,
                    src: vehicleImages.image,
                  }}
                  extension={{
                    type: 'download',
                  }}
                />
              );
            })}
          </Flex>
        </>
      ) : null}
      {previewBeforeUploadOpen ? (
        <UploadDocumentImageConfirmation
          confirmationHandler={() => {
            uploadImageHandler({ imageData: uploadImageData! });
          }}
          editHandler={editUploadedImageOpenHandler}
          image={uploadImageData!}
          onClose={previewBeforeUploadClose}
          retakeHandler={retakeNewImageOpenHandler}
        />
      ) : null}
      {editUploadedImageOpen ? (
        <EditImage
          image={uploadImageData?.url!}
          onClose={editUploadedImageClose}
          onSave={(data) => {
            setUploadImageData(data);

            editUploadedImageClose();
            previewBeforeUploadOpenHandler();
          }}
        />
      ) : null}
      {retakeUploadedImageOpen && (
        <UploadDocumentCameraPage
          onDenyCamera={() => {}}
          updateImage={(data) => {
            setUploadImageData(data);
            retakeUploadedImageClose();
            editAndUploadImage({
              imageData: data,
              closeModal: retakeUploadedImageClose,
              imageId: retakePhotoId!,
            });
          }}
          closeCamera={retakeUploadedImageClose}
        />
      )}
      {retakeNewImageOpen && (
        <UploadDocumentCameraPage
          onDenyCamera={() => {}}
          updateImage={(data) => {
            setUploadImageData(data);
            previewBeforeUploadOpenHandler();
            retakeNewImageClose();
          }}
          closeCamera={retakeUploadedImageClose}
        />
      )}
    </>
  );
};

export default DamagedAttachedImagesTab;
